import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, API } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calculator, FileDown, UploadCloud, Sparkles, AlertCircle, Eye } from "lucide-react";
import { generatePayslipPDF } from "@/lib/payslip";
import { useBareme } from "@/hooks/useBareme";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

async function loadLogoDataUrl(entId) {
  try {
    const res = await fetch(`${API}/files/logo/${entId}`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

export default function GenerateBulletinDialog({ open, onOpenChange, salaries, onUploaded }) {
  const [salarie, setSalarie] = useState("");
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [profile, setProfile] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [calc, setCalc] = useState(null);
  const [loading, setLoading] = useState(false);
  // Lot B v2 - heures sup + primes détaillées
  const [hs25, setHs25] = useState("");
  const [hs50, setHs50] = useState("");
  const [primePanier, setPrimePanier] = useState("");
  const [primeTransport, setPrimeTransport] = useState("");
  const [primePerf, setPrimePerf] = useState("");
  const [primeAnciennete, setPrimeAnciennete] = useState("");
  const [retenues, setRetenues] = useState("");
  const [netManual, setNetManual] = useState("");
  const [detailedCotis, setDetailedCotis] = useState(false);
  const [deductTr, setDeductTr] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Load profile on open
  useEffect(() => {
    if (!open) return;
    api.get("/chef/entreprise/profile").then(async (r) => {
      setProfile(r.data);
      if (r.data.logo_path) {
        const url = await loadLogoDataUrl(r.data.id);
        setLogoDataUrl(url);
      } else {
        setLogoDataUrl(null);
      }
    }).catch(() => {});
  }, [open]);

  // Auto preview when params change
  useEffect(() => {
    if (!salarie || !open) { setCalc(null); return; }
    setLoading(true);
    api.get("/chef/paie/preview", { params: { salarie_id: salarie, mois, annee } })
      .then((r) => setCalc(r.data))
      .catch(() => setCalc(null))
      .finally(() => setLoading(false));
  }, [salarie, mois, annee, open]);

  const sal = salaries.find((s) => s.id === salarie);
  const { values: bareme, meta: baremeMeta } = useBareme(`${annee}-${String(mois).padStart(2, "0")}-01`);
  const tauxCot = profile?.taux_cotisations ?? bareme.taux_cotisations_defaut ?? 22;
  const baseBrut = calc?.brut_calcule || 0;
  const taux = sal?.taux_horaire || 0;
  const hs25Num = parseFloat(hs25) || 0;
  const hs50Num = parseFloat(hs50) || 0;
  const hs25Amount = +(hs25Num * taux * 1.25).toFixed(2);
  const hs50Amount = +(hs50Num * taux * 1.5).toFixed(2);
  const primesNum = (parseFloat(primePanier) || 0) + (parseFloat(primeTransport) || 0) + (parseFloat(primePerf) || 0) + (parseFloat(primeAnciennete) || 0);
  const retenuesNum = parseFloat(retenues) || 0;
  const finalBrut = +(baseBrut + hs25Amount + hs50Amount + primesNum - retenuesNum).toFixed(2);

  // Cotisations détaillées : taux salariaux du barème réglementaire en vigueur pour la période (Regulatory Engine)
  const cotisationsDetail = useMemo(() => {
    if (!detailedCotis) return null;
    const s = (k) => (bareme[k] && typeof bareme[k] === "object" ? Number(bareme[k].sal) || 0 : 0);
    const abattement = (Number(bareme.csg_abattement) || 98.25) / 100;
    return [
      { label: "Sécurité sociale - Maladie/Maternité", taux: s("maladie") },
      { label: "Sécurité sociale - Vieillesse plafonnée", taux: s("vieillesse_plaf") },
      { label: "Sécurité sociale - Vieillesse déplafonnée", taux: s("vieillesse_deplaf") },
      { label: "Retraite complémentaire AGIRC-ARRCO T1", taux: s("retraite_t1") },
      { label: "Contribution d'équilibre général (CEG)", taux: s("ceg_t1") },
      { label: "Contribution équilibre technique (CET)", taux: s("cet") },
      { label: "CSG déductible (sur 98,25 % du brut)", taux: +(s("csg_deductible") * abattement).toFixed(3) },
      { label: "CSG non déductible", taux: +(s("csg_non_deductible") * abattement).toFixed(3) },
      { label: "CRDS", taux: +(s("crds") * abattement).toFixed(3) },
    ];
  }, [detailedCotis, bareme]);

  const cotisations = cotisationsDetail
    ? +cotisationsDetail.reduce((s, c) => s + finalBrut * (c.taux / 100), 0).toFixed(2)
    : +(finalBrut * (tauxCot / 100)).toFixed(2);
  const netAuto = +(finalBrut - cotisations).toFixed(2);
  const tr = calc?.titres_restaurant;
  const trPart = deductTr && tr?.enabled && tr.tickets > 0 ? +Number(tr.part_salarie || 0).toFixed(2) : 0;
  const netAutoTr = +(netAuto - trPart).toFixed(2);
  const finalNet = netManual !== "" ? parseFloat(netManual) : netAutoTr;
  const trCalc = useMemo(() => (trPart > 0 ? { ...tr, part_salarie: trPart } : null), [tr, trPart]);

  const profileMissing = !profile || !profile.adresse || !profile.siret;

  // Live PDF preview as a blob URL (regenerate on any change)
  const previewUrl = useMemo(() => {
    if (!sal || !calc || !profile) return null;
    try {
      const doc = generatePayslipPDF({
        entreprise: profile,
        logoDataUrl,
        salarie: sal,
        periode: { mois, annee },
        calcul: {
          ...calc,
          brut_calcule: finalBrut,
          hs25: hs25Num,
          hs50: hs50Num,
          primes_detail: {
            panier: parseFloat(primePanier) || 0,
            transport: parseFloat(primeTransport) || 0,
            performance: parseFloat(primePerf) || 0,
            anciennete: parseFloat(primeAnciennete) || 0,
          },
          primes: primesNum,
          retenues: retenuesNum,
          titres_restaurant: trCalc,
          cotisations_detail: cotisationsDetail,
          net_a_payer: finalNet,
        },
        breakdown: calc.breakdown,
      });
      const blob = doc.output("blob");
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  }, [sal, calc, profile, logoDataUrl, mois, annee, finalBrut, hs25Num, hs50Num, primePanier, primeTransport, primePerf, primeAnciennete, primesNum, retenuesNum, finalNet, cotisationsDetail, trCalc]);

  // Cleanup blob URLs on change
  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const handleGenerate = async (uploadAfter) => {
    if (!sal || !calc) return;
    setGenerating(true);
    try {
      const doc = generatePayslipPDF({
        entreprise: profile,
        logoDataUrl,
        salarie: sal,
        periode: { mois, annee },
        calcul: {
          ...calc,
          brut_calcule: finalBrut,
          hs25: hs25Num,
          hs50: hs50Num,
          primes_detail: {
            panier: parseFloat(primePanier) || 0,
            transport: parseFloat(primeTransport) || 0,
            performance: parseFloat(primePerf) || 0,
            anciennete: parseFloat(primeAnciennete) || 0,
          },
          primes: primesNum,
          retenues: retenuesNum,
          titres_restaurant: trCalc,
          cotisations_detail: cotisationsDetail,
          net_a_payer: finalNet,
        },
        breakdown: calc.breakdown,
      });
      const filename = `bulletin-${sal.code}-${MONTHS[mois - 1].toLowerCase()}-${annee}.pdf`;
      if (uploadAfter) {
        const blob = doc.output("blob");
        const fd = new FormData();
        fd.append("file", blob, filename);
        const params = new URLSearchParams({ salarie_id: salarie, mois: String(mois), annee: String(annee), brut: String(finalBrut) });
        await api.post(`/chef/bulletins?${params.toString()}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Bulletin généré et déposé pour le salarié");
        onUploaded?.();
        onOpenChange?.(false);
      } else {
        doc.save(filename);
        toast.success("Bulletin téléchargé");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-6xl max-h-[95vh] overflow-hidden p-0 flex flex-col">
        <DialogHeader className="p-6 pb-3 border-b border-[#DEE2E6]">
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Générer un bulletin de paie</DialogTitle>
        </DialogHeader>

        <div className="grid lg:grid-cols-2 flex-1 overflow-hidden">
          {/* LEFT: form */}
          <div className="p-6 overflow-y-auto border-r border-[#DEE2E6]">
            {profileMissing && (
              <div className="p-3 mb-4 border border-[#FCD34D] bg-[#FFFBEB] flex items-start gap-2 text-xs text-[#92400E]" data-testid="profile-warning">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  Profil entreprise incomplet (logo, adresse, SIRET).{" "}
                  <a href="/chef/profil-entreprise" className="font-bold underline">Compléter →</a>
                </div>
              </div>
            )}

            <div className="space-y-4" data-testid="generate-bulletin-form">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Salarié</Label>
                <Select value={salarie} onValueChange={setSalarie}>
                  <SelectTrigger data-testid="gen-salarie" className="h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    {salaries.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.prenom} {s.nom} · {s.poste} · {s.taux_horaire}€/h
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Mois</Label>
                  <Select value={String(mois)} onValueChange={(v) => setMois(Number(v))}>
                    <SelectTrigger data-testid="gen-mois" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Année</Label>
                  <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
                    <SelectTrigger data-testid="gen-annee" className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* Heures supplémentaires (Lot B v2) */}
              <div className="border border-[#DEE2E6] p-3 bg-[#F8F9FA]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2 text-[#002FA7]">Heures supplémentaires</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">HS +25% (h)</Label>
                    <Input type="number" step="0.5" value={hs25} onChange={(e) => setHs25(e.target.value)} data-testid="gen-hs25" placeholder="0" className="h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">HS +50% (h)</Label>
                    <Input type="number" step="0.5" value={hs50} onChange={(e) => setHs50(e.target.value)} data-testid="gen-hs50" placeholder="0" className="h-10 mt-1" />
                  </div>
                </div>
                {(hs25Num + hs50Num) > 0 && (
                  <div className="text-[10px] text-[#495057] mt-2 font-mono">
                    Majoration : +{(hs25Amount + hs50Amount).toFixed(2)}€
                  </div>
                )}
              </div>

              {/* Primes détaillées (Lot B v2) */}
              <div className="border border-[#DEE2E6] p-3 bg-[#F8F9FA]">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2 text-[#002FA7]">Primes & indemnités</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-semibold">Panier (€)</Label>
                    <Input type="number" step="0.01" value={primePanier} onChange={(e) => setPrimePanier(e.target.value)} data-testid="gen-prime-panier" placeholder="0.00" className="h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Transport (€)</Label>
                    <Input type="number" step="0.01" value={primeTransport} onChange={(e) => setPrimeTransport(e.target.value)} data-testid="gen-prime-transport" placeholder="0.00" className="h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Performance (€)</Label>
                    <Input type="number" step="0.01" value={primePerf} onChange={(e) => setPrimePerf(e.target.value)} data-testid="gen-prime-perf" placeholder="0.00" className="h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold">Ancienneté (€)</Label>
                    <Input type="number" step="0.01" value={primeAnciennete} onChange={(e) => setPrimeAnciennete(e.target.value)} data-testid="gen-prime-anciennete" placeholder="0.00" className="h-10 mt-1" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Retenues (€)</Label>
                  <Input type="number" step="0.01" value={retenues} onChange={(e) => setRetenues(e.target.value)} data-testid="gen-retenues" placeholder="0.00" className="h-11" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Net manuel</Label>
                  <Input type="number" step="0.01" value={netManual} onChange={(e) => setNetManual(e.target.value)} data-testid="gen-net-manual" placeholder={netAutoTr.toFixed(2)} className="h-11" />
                </div>
              </div>

              {tr?.enabled && tr.tickets > 0 && (
                <div className="flex items-center gap-2 p-3 border border-[#FFD700] bg-[#FFFBEB]" data-testid="gen-tr-block">
                  <input type="checkbox" id="deduct-tr" checked={deductTr} onChange={(e) => setDeductTr(e.target.checked)} data-testid="gen-tr-deduct" className="h-4 w-4 accent-[#002FA7]" />
                  <label htmlFor="deduct-tr" className="text-xs font-semibold cursor-pointer flex-1">
                    Titres-restaurant : <span data-testid="gen-tr-tickets">{tr.tickets}</span> titre(s) × {Number(tr.amounts?.valeur_faciale || 0).toFixed(2)} € — part salariale <span data-testid="gen-tr-part">{Number(tr.part_salarie || 0).toFixed(2)} €</span> retenue sur le net
                  </label>
                  <span className="text-[10px] text-[#868E96]">{tr.validated ? "mois validé" : "calcul en direct"}</span>
                </div>
              )}

              <div className="flex items-center gap-2 p-3 border border-[#DEE2E6] bg-white">
                <input type="checkbox" id="detailed-cotis" checked={detailedCotis} onChange={(e) => setDetailedCotis(e.target.checked)} data-testid="gen-detailed-cotis" className="h-4 w-4 accent-[#002FA7]" />
                <label htmlFor="detailed-cotis" className="text-xs font-semibold cursor-pointer">
                  Cotisations détaillées (URSSAF + AGIRC-ARRCO + CSG/CRDS)
                </label>
                {baremeMeta && <span className="ml-auto text-[10px] text-[#868E96]" data-testid="gen-bareme">Barème au {new Date(baremeMeta.at + "T00:00:00").toLocaleDateString("fr-FR")}</span>}
              </div>

              {/* Récap */}
              {salarie && (
                <div className="border border-[#002FA7] bg-[#F8F9FA] p-4" data-testid="gen-recap">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="h-4 w-4 text-[#002FA7]" />
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">
                      Récap (cotis. {tauxCot}%)
                    </div>
                    <Sparkles className="h-3 w-3 text-[#002FA7] ml-auto" />
                  </div>
                  {loading ? <div className="text-sm text-[#868E96]">Calcul…</div> : calc ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <Stat label="Heures" value={calc.total_heures.toFixed(2)} unit="h" />
                      <Stat label="Brut final" value={finalBrut.toFixed(2)} unit="€" />
                      <Stat label="Cotisations" value={cotisations.toFixed(2)} unit="€" muted />
                      <Stat label="NET" value={finalNet.toFixed(2)} unit="€" highlight />
                    </div>
                  ) : (
                    <div className="text-sm text-[#868E96]">Aucune donnée de planning pour cette période.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: live preview */}
          <div className="bg-[#F3F4F6] flex flex-col overflow-hidden" data-testid="pdf-preview-pane">
            <div className="px-4 py-2 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.25em] font-semibold flex items-center gap-2">
              <Eye className="h-3.5 w-3.5 text-[#FFD700]" />
              Aperçu temps réel
              {previewUrl && <span className="ml-auto text-[10px] opacity-60 normal-case tracking-normal">Mise à jour automatique</span>}
            </div>
            <div className="flex-1 overflow-hidden bg-[#525659]">
              {previewUrl ? (
                <iframe
                  key={previewUrl}
                  src={previewUrl}
                  title="Aperçu bulletin de paie"
                  className="w-full h-full border-0"
                  data-testid="pdf-preview-frame"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-white/60 text-sm p-8 text-center">
                  {!salarie ? "Sélectionnez un salarié pour générer l'aperçu." : loading ? "Chargement…" : "Aucune donnée disponible pour cette période."}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="p-4 border-t border-[#DEE2E6] gap-2 flex-wrap sm:flex-nowrap">
          <Button type="button" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="gen-cancel">Annuler</Button>
          <Button type="button" onClick={() => handleGenerate(false)} disabled={!salarie || !calc || generating} data-testid="gen-download" variant="outline" className="border-[#0A0A0A]">
            <FileDown className="h-4 w-4 mr-2" /> Télécharger le PDF
          </Button>
          <Button type="button" onClick={() => handleGenerate(true)} disabled={!salarie || !calc || generating} data-testid="gen-upload" className="bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
            <UploadCloud className="h-4 w-4 mr-2" /> {generating ? "Dépôt…" : "Générer et déposer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, unit, highlight, muted }) {
  return (
    <div className={`p-3 border ${highlight ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : muted ? "bg-[#F3F4F6] border-[#DEE2E6] text-[#495057]" : "bg-white border-[#DEE2E6]"}`}>
      <div className={`text-[9px] uppercase tracking-[0.2em] font-semibold ${highlight ? "text-[#FFD700]" : "text-[#868E96]"}`}>{label}</div>
      <div className="font-display text-lg font-black tracking-tight mt-1">
        {value}<span className="text-xs ml-1 opacity-70 font-normal">{unit}</span>
      </div>
    </div>
  );
}
